import { useEffect, useState, useRef, memo } from 'react';
import { TextField, CircularProgress } from '@mui/material';
import { BaseInput, Icon, TextInput, CheckBox, CustomTooltip } from '@core-web/ui';
import { IconNames } from '@core-web/ui/Icon';
import { IDropdown } from './IDropdown';
import { useFormContext, useController } from 'react-hook-form';
import { addScrollbarClasses } from '@core-web/lib';
import { useDebouncedCallback } from '@core-web/hooks';
import * as S from './Dropdown.styled';
import { ModalSizes } from '@echotex/business-modules/components/GlobalModal/enums';
import { useGlobalModalOuterContext } from '@echotex/business-modules/components/GlobalModal/context';

export const BaseDropdown = memo((props: IDropdown): JSX.Element => {
  // Destructure Props
  const {
    apiHook,
    id,
    placeHolder,
    name,
    isAsync,
    preFetchedOptions = [],
    getQueryParams,
    readOnlyBox,
    setFetchedOption,
    onValueChange,
    withRequiredBorder,
    isPreFetchLoading,
    isDisabled,
    dependency,
    dependentFields,
    disableClearable,
    checkBoxConfiguration,
    dynamicOptionCallback,
    defaultValue,
    isOptionEqualToValue = true,
    getLiveData,
    onChange,
    disablePortal,
    error,
    skipDefaultReset,
    showTooltip = false,
    addNewConfig,
  } = props;

  //Component States
  const [options, setOptions] = useState<unknown[]>([]);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [hasValue, setHasValue] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [inputFieldValue, setInputFieldValue] = useState<string>('');
  const [prevParentCache, setParentCache] = useState<any>(null);
  const parentRef = useRef<any>(null);

  // React Hook Form
  const methods = useFormContext();
  const { control, setValue, resetField, clearErrors } = methods;
  const { field } = useController({
    control,
    name,
    defaultValue,
  });
  const { setGlobalModalOuterProps } = useGlobalModalOuterContext();
  const fieldValue = field.value;
  //const dependencyValueRaw = useWatch({ name: dependency || `${name}-dependency`, control });
  const dependencyValueRaw = methods.watch(dependency || `${name}-dependency`); // due to issue in the bom we are using watch here
  const dependencyValue =
    dependencyValueRaw && !dependencyValueRaw?.value
      ? { label: dependencyValueRaw, value: dependencyValueRaw }
      : dependencyValueRaw;

  // Local Variables
  const isValueFetchableOnPress = apiHook && isAsync;

  //Side Effects
  useEffect(() => {
    if (apiHook && !isAsync && setFetchedOption && !getLiveData && !dependency) {
      let qParam = null;
      setLoading(true);

      if (getQueryParams) {
        qParam = getQueryParams();
      }

      apiHook.callAsyncApi(qParam).then((res) => {
        if (res?.data) {
          setOptions([...setFetchedOption(res.data, name)]);
        } else {
          setOptions([]);
        }
        setLoading(false);
      });
    }
  }, []);

  useEffect(() => {
    if (!apiHook) {
      setLoading(!!isPreFetchLoading);
    }
  }, [isPreFetchLoading]);

  useEffect(() => {
    if (!apiHook) {
      if (setFetchedOption) {
        setOptions([...setFetchedOption(preFetchedOptions, name)]);
      } else {
        setOptions([...preFetchedOptions]);
      }
    }
  }, [preFetchedOptions?.length]);

  useEffect(() => {
    if (!skipDefaultReset) {
      resetField(name, { defaultValue });
    }
  }, [defaultValue]);

  useEffect(() => {
    if (dependency && prevParentCache && prevParentCache?.value !== dependencyValue?.value) {
      setValue(name, null);
      if (readOnlyBox?.name) {
        resetField(readOnlyBox.name);
      }
      clearErrors(name);
    }
    if (apiHook && !isAsync && dependency && prevParentCache?.value !== dependencyValue?.value && setFetchedOption) {
      if (dependencyValue?.value) {
        let qParam = null;
        setLoading(true);

        if (getQueryParams) {
          qParam = getQueryParams(dependencyValue);
        }

        apiHook.callAsyncApi(qParam).then((res) => {
          if (res?.data) {
            setOptions([...setFetchedOption(res.data, name)]);
          } else {
            setOptions([]);
          }
          setLoading(false);
        });
      } else {
        setOptions([]);
      }
    }

    setParentCache(dependencyValue);
  }, [dependency, dependencyValue?.value]);

  useEffect(() => {
    const isError = !!error;
    setHasError(isError);
  }, [error]);

  useEffect(() => {
    setHasValue(!!fieldValue);

    if (!fieldValue && readOnlyBox) {
      setValue(readOnlyBox.name, '');
    }

    // need to remove this later
    // if (!fieldValue) {
    //   parentRef?.current?.focus();
    // }
  }, [fieldValue]);

  useEffect(() => {
    if (apiHook && isAsync && setFetchedOption && !apiHook.isLoading && apiHook.isSuccess) {
      if (!Array.isArray(apiHook?.data) && !Array.isArray(apiHook?.data?.list)) {
        setOptions([]);
      } else {
        setOptions([...setFetchedOption(apiHook?.data, name)]);
      }

      setLoading(false);
    }
  }, [apiHook?.isLoading, apiHook?.isSuccess]);

  useEffect(() => {
    if (apiHook && isAsync && setFetchedOption && !apiHook.isLoading && apiHook.isError) {
      setOptions([]);
      setLoading(false);
    }
  }, [apiHook?.isLoading, apiHook?.isError]);

  useEffect(() => {
    if (readOnlyBox?.value) {
      setValue(readOnlyBox.name, readOnlyBox.value);
    }
  }, [readOnlyBox?.value]);

  useEffect(() => {
    addScrollbarClasses('.MuiAutocomplete-listbox');
  }, []);

  const fetchOptions = () => {
    if (dependency && dynamicOptionCallback) {
      // if the dropdown has dependency
      // and there is a callback provided to fill up the option
      // list from another input value then this
      // condition will be triggered
      // this mostly wil be used for multiple grid form component
      setOptions(dynamicOptionCallback(dependencyValue));
    } else if (!apiHook && !isAsync && preFetchedOptions) {
      // synch way of fetching data
      // assuming we have some pre fetched list as preRenderedOptions
      if (setFetchedOption) {
        setOptions([...setFetchedOption(preFetchedOptions, name)]);
      } else {
        setOptions([...preFetchedOptions]);
      }
    } else if (apiHook && !isAsync && setFetchedOption && getLiveData) {
      let qParam = null;
      setLoading(true);

      if (getQueryParams) {
        qParam = getQueryParams(dependencyValue);
      }

      apiHook.callAsyncApi(qParam).then((res) => {
        if (res?.data) {
          setOptions([...setFetchedOption(res.data, name)]);
        } else {
          setOptions([]);
        }
        setLoading(false);
      });
    } else if (apiHook && isAsync && setFetchedOption) {
      // async way of fetching initial data when dropdown opens
      let qParam = null;
      setLoading(true);

      if (getQueryParams && addNewConfig) {
        qParam = getQueryParams(''); // Call with empty string for initial load
      }

      if (qParam) {
        apiHook.callAsyncApi(qParam).then((res) => {
          if (res?.data) {
            setOptions([...setFetchedOption(res.data, name)]);
          } else {
            setOptions([]);
          }
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    }
  };
  const handleOnKeyUp = (event: any) => {
    const inputFieldValue = event.target.value;
    setInputFieldValue(inputFieldValue);
    if (isValueFetchableOnPress) {
      if (inputFieldValue) {
        // this is aSynch way of fetching data
        // we have a apiHook sent by the wrapper that we can
        // use to fetch data
        setLoading(true);
        if (dependency) {
          const qParam = getQueryParams?.(dependencyValue, inputFieldValue);
          qParam && apiHook.callApi(qParam);
        } else {
          const qParam = getQueryParams?.(inputFieldValue);
          qParam && apiHook.callApi(qParam);
        }
      } else {
        // if input value is empty then setting the option empty
        setOptions([]);
        setLoading(false);
      }
    }
  };

  const handleKeyUpWithDebounce = useDebouncedCallback((e: any) => {
    handleOnKeyUp(e);
  }, 300);

  const clearOptions = () => {
    if (isValueFetchableOnPress || !apiHook || getLiveData) {
      setOptions([]);
    }
  };

  const handleChange = (event: any, data: any) => {
    const e: any = {
      target: { value: data },
    };
    field.onChange(data);
    //field.onChange(data ? data.value : null);
    onChange?.(e, dependentFields);
    onValueChange?.(data, dependentFields);
    //trigger(name);
    parentRef?.current?.focus();
  };

  const handleIsOptionEqualToValue = (option: any, selectedOption: any): boolean => {
    return isOptionEqualToValue || option.value === selectedOption.value;
  };

  const handleAddButtonClick = () => {
    if (parentRef.current) {
      const input = parentRef.current.querySelector('input');
      if (input) {
        input.blur();
      }
    }
    if (addNewConfig?.modalComponent) {
      setGlobalModalOuterProps({
        isOpen: true,
        children: addNewConfig?.modalComponent,
        fieldName: name,
        parentFormMethods: methods,
        size: addNewConfig?.modalSize || ModalSizes.MEDIUM,
        modalData: {
          inputValue: inputFieldValue,
          fieldName: name,
          dependentFields,
          ...addNewConfig?.modalData,
        },
        callBacks: {
          ...(addNewConfig?.onRefresh && { refresh: addNewConfig.onRefresh }),
          ...addNewConfig?.callBacks,
        },
      });
    }
    addNewConfig?.onAddNew?.(inputFieldValue, name, dependentFields);
  };

  // Don't remove this. We will use it for debugging
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleClick = () => {
    setTimeout(() => {
      debugger;
    }, 2000);
  };

  const className = `${withRequiredBorder ? 'withRequiredBorder' : ''}${hasValue ? ' hasValue ' : ''} ${
    hasError ? ' hasError' : ''
  }${
    (inputFieldValue === '' || inputFieldValue === null || inputFieldValue === 'undefined') &&
    isValueFetchableOnPress &&
    !isLoading
      ? ' hideOptionWrapper'
      : ''
  }`;
  const valueToRender = fieldValue?.label;

  const customNoOptionsText = (
    <>
      <S.NoDataText>No Data Available</S.NoDataText>
      {addNewConfig?.enabled && (
        <S.AddNewButtonWrapper onClick={handleAddButtonClick}>
          {addNewConfig.buttonText || `Add new ${placeHolder?.toLowerCase() || 'item'}`}
        </S.AddNewButtonWrapper>
      )}
    </>
  );

  return (
    <S.AutocompleteWrapper ref={parentRef} tabIndex={-1} className='myDropdown'>
      <S.MuiAutocomplete
        disablePortal={disablePortal}
        id={id}
        options={options}
        onOpen={fetchOptions}
        onClose={clearOptions}
        className={className}
        loading={isLoading}
        renderInput={(params) => (
          <CustomTooltip content={valueToRender} showTooltip={showTooltip}>
            <TextField
              {...params}
              placeholder={placeHolder}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress size={12} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
                autoComplete: 'new-password',
              }}
              onKeyUp={handleKeyUpWithDebounce}
              autoComplete={'none'}
              // onClick={handleClick}
            />
          </CustomTooltip>
        )}
        PopperComponent={(props) => <S.CustomPopper {...props} className={`${props.className} custom-popper-class`} />}
        renderOption={(props, option: any, state: any) => (
          <li {...props} key={`${name}-${option.value}-${state?.index}`}>
            <S.MuiListItemContent>{option.label}</S.MuiListItemContent>
          </li>
        )}
        noOptionsText={customNoOptionsText}
        popupIcon={<Icon icon={IconNames.DOWN_ARROW} />}
        {...field}
        value={field.value ?? null}
        onChange={handleChange}
        isOptionEqualToValue={handleIsOptionEqualToValue}
        getOptionLabel={(opt: any) => String(opt?.label ?? '')}
        disabled={isDisabled || props?.disabled || (!isAsync && isLoading)}
        disableClearable={disableClearable}
      />
      {readOnlyBox && (
        <S.AutoCompleteValue size={readOnlyBox.size || 40}>
          <TextInput name={readOnlyBox.name} readOnly={true} placeHolder={readOnlyBox.placeHolder} />
          {/* <S.ReadonlyBoxField {...readonlyBoxField.field} inputProps={{ readOnly: true }} /> */}
        </S.AutoCompleteValue>
      )}
      {checkBoxConfiguration && (
        <S.CheckBoxWrapper>
          <CheckBox name={checkBoxConfiguration.name} value={'true'} size={20} />
        </S.CheckBoxWrapper>
      )}
    </S.AutocompleteWrapper>
  );
});

BaseDropdown.displayName = 'BaseDropdown';

const Dropdown = (props: IDropdown): JSX.Element => {
  return <BaseInput {...props} input={BaseDropdown} />;
};

export default Dropdown;
